import {
  WEBSITE_NAV_PAGE_KEYS,
  websiteEditorPageLabel,
} from "./websiteEditorNav";
import { publicPathRestForTemplatePage } from "./websitePublicTemplatePath";
import { buildCreatorPagePath } from "../../../Utils/url/getCreatorSpaRelativePath";
import { StaticVariables } from "../../../Utils/StaticVariables";
import type { SectionCfg } from "./types";

export const NAV_ORDER_EDIT_KEY = "nav.order";
export const NAV_OMIT_EDIT_KEY = "nav.omit";

/** Link in Bio is live at its own URL, but it stays out of site nav unless added. */
export const DEFAULT_NAV_OMIT = ["linkInBio"] as const;

/** Max page links shown inline before the rest go under More (More itself is extra). */
export const NAV_VISIBLE_INLINE = 7;
/** Hard cap on header items (visible + overflow). */
export const NAV_TOTAL_MAX = 11;
/** Max characters for a page / nav label (header + create/edit forms). */
export const NAV_LABEL_MAX = 20;

export function clampNavLabel(label: string): string {
  return String(label ?? "").slice(0, NAV_LABEL_MAX);
}

export type NavDesktopLayout = "standard" | "flank" | "stacked" | "minimal";
export type NavMobileLayout = "fullscreen" | "drawer";

export const NAV_DESKTOP_LAYOUT_OPTIONS: {
  id: NavDesktopLayout;
  label: string;
  hint: string;
}[] = [
  { id: "standard", label: "Standard", hint: "Wordmark left, links right — up to 7 inline, rest in More" },
  { id: "flank", label: "Split", hint: "Wordmark centered, links flanking — up to 7 inline, rest in More" },
  { id: "stacked", label: "Stacked", hint: "Centered brand over links — up to 7 inline, rest in More" },
  {
    id: "minimal",
    label: "Minimal",
    hint: "Brand + menu button — every page in the panel, no item cap",
  },
];

/** Stacked stays implemented for existing sites; hide it from the picker for now. */
export const NAV_DESKTOP_LAYOUT_PICKER_OPTIONS = NAV_DESKTOP_LAYOUT_OPTIONS.filter(
  (o) => o.id !== "stacked",
);

export const NAV_MOBILE_LAYOUT_OPTIONS: {
  id: NavMobileLayout;
  label: string;
  hint: string;
}[] = [
  {
    id: "drawer",
    label: "Drawer",
    hint: "Default — slides in from the right over a dimmed page; every page listed",
  },
  {
    id: "fullscreen",
    label: "Full screen",
    hint: "Large type, every page, type scales so it never scrolls",
  },
];

/**
 * Legacy `split` / `serif-mark` were wordmark-left (Standard).
 * `underline` was the flanking layout now labeled Split.
 */
export function resolveNavDesktopLayout(
  style: string | undefined | null,
): NavDesktopLayout {
  const s = String(style || "").trim();
  if (s === "minimal") return "minimal";
  if (s === "stacked" || s === "centered") return "stacked";
  if (s === "flank" || s === "underline") return "flank";
  return "standard";
}

export function resolveNavMobileLayout(
  style: string | undefined | null,
): NavMobileLayout {
  // Default is the right-side drawer; only opt into fullscreen explicitly.
  return String(style || "").trim() === "fullscreen" ? "fullscreen" : "drawer";
}

export function splitNavOverflow<T>(items: T[]): { visible: T[]; overflow: T[] } {
  // Cap is 7 *pages* inline. More is an extra control outside that count.
  if (items.length <= NAV_VISIBLE_INLINE) return { visible: items, overflow: [] };
  return {
    visible: items.slice(0, NAV_VISIBLE_INLINE),
    overflow: items.slice(NAV_VISIBLE_INLINE),
  };
}

/**
 * Count cap (7 pages inline; More is extra) plus a width budget so long
 * labels collapse into More before the header wraps. `availablePx` is the
 * links-row budget (search reserved via `trailingPx`).
 */
export function splitNavOverflowWithBudget<T>(
  items: T[],
  getLabel: (item: T) => string,
  availablePx: number | null | undefined,
  opts?: {
    gapPx?: number;
    morePx?: number;
    trailingPx?: number;
    charPx?: number;
  },
): { visible: T[]; overflow: T[] } {
  if (items.length === 0) return { visible: [], overflow: [] };

  const countSplit = splitNavOverflow(items);
  if (availablePx == null || !(availablePx > 0)) return countSplit;

  const gapPx = opts?.gapPx ?? 14;
  const morePx = opts?.morePx ?? 58;
  const trailingPx = opts?.trailingPx ?? 28;
  const charPx = opts?.charPx ?? 8.2;

  const widths = items.map((item) => {
    const label = clampNavLabel(getLabel(item));
    return Math.max(28, label.length * charPx + 4);
  });

  const hardInlineMax = countSplit.visible.length;
  let budget = availablePx - trailingPx;
  if (!(budget > 0)) {
    return {
      visible: items.slice(0, 1),
      overflow: items.slice(1),
    };
  }

  let visibleCount = 0;
  let used = 0;
  for (let i = 0; i < items.length && visibleCount < hardInlineMax; i++) {
    const othersLeft = items.length - (i + 1);
    const itemW = widths[i] + (visibleCount > 0 ? gapPx : 0);
    // Reserve More whenever anything would remain after this pick.
    const reserveMore = othersLeft > 0 ? gapPx + morePx : 0;
    if (used + itemW + reserveMore <= budget) {
      used += itemW;
      visibleCount += 1;
    } else {
      break;
    }
  }

  if (visibleCount === items.length) {
    return { visible: items, overflow: [] };
  }
  if (visibleCount === 0) visibleCount = 1;

  return {
    visible: items.slice(0, visibleCount),
    overflow: items.slice(visibleCount),
  };
}

type WebsitePageUrlOpts = {
  /** Unpublished sites live under `/website/:user` (demo), not `/:user`. */
  demo?: boolean;
  /** Published/demo share origin, including custom domain when set. */
  shareBase?: string;
};

function joinShareBaseAndRest(base: string, rest: string): string {
  const root = String(base || "").replace(/\/+$/, "");
  const path = String(rest || "").replace(/^\/+/, "");
  if (!root) return "";
  if (!path) return root;
  return `${root}/${path}`;
}

/** Public creator URL for the inspector “open in new tab” / copy controls. */
export function websitePageTabUrl(
  username: string | undefined,
  slug: string,
  opts?: WebsitePageUrlOpts,
): string {
  const rest = String(slug || "").replace(/^\//, "");
  if (opts?.shareBase) {
    return joinShareBaseAndRest(opts.shareBase, rest);
  }
  const user = String(username || "").trim().toLowerCase();
  if (!user) return "";
  const path = buildCreatorPagePath({
    username: user,
    route: rest || undefined,
    demo: opts?.demo,
  });
  if (!path) return "";
  if (typeof window === "undefined") return path;
  return `${window.location.origin.replace(/\/+$/, "")}${path}`;
}

/**
 * Nav link keys shown in Site Header (includes Link in Bio + Search).
 * Home stays first; Search stays last; middle pages keep their saved order.
 */
export const SITE_HEADER_NAV_KEYS = [
  "home",
  "search",
  "shop",
  "blog",
  "about",
  "contact",
  "content",
  "linkInBio",
] as const;

export type SiteHeaderNavKey = (typeof SITE_HEADER_NAV_KEYS)[number];

/**
 * Fixed ends of Site Header nav edit — not reorderable / not editable.
 * Home is pinned first; Search is pinned last (when visible). Search can
 * still be hidden / shown; Home cannot.
 */
export const SITE_HEADER_PINNED_NAV_KEYS = ["home", "search"] as const;

export function isSiteHeaderPinnedNavKey(key: string): boolean {
  return (SITE_HEADER_PINNED_NAV_KEYS as readonly string[]).includes(key);
}

/** Only Home is permanently in nav (Search can be hidden / shown). */
export function isSiteHeaderAlwaysVisibleNavKey(key: string): boolean {
  return key === "home";
}

/** Keep Home first and Search last; preserve relative order of the middle. */
export function pinSiteHeaderNavOrder(keys: string[]): string[] {
  const hasSearch = keys.includes("search");
  const middle = keys.filter((k) => k !== "home" && k !== "search");
  return ["home", ...middle, ...(hasSearch ? ["search"] : [])];
}

export const SITE_HEADER_NAV_DEFAULTS: Record<
  SiteHeaderNavKey,
  { label: string; pageKey: string }
> = {
  home: { label: "Home", pageKey: "home" },
  search: { label: "Search", pageKey: "search" },
  shop: { label: "Products", pageKey: "shop" },
  blog: { label: "Blog", pageKey: "journal" },
  about: { label: "About", pageKey: "about" },
  contact: { label: "Contact", pageKey: "contact" },
  content: { label: "Content", pageKey: "content" },
  linkInBio: { label: "Link in Bio", pageKey: "linkInBio" },
};

export function navKeyToPageKey(navKey: string): string {
  if (navKey === "blog") return "journal";
  return SITE_HEADER_NAV_DEFAULTS[navKey as SiteHeaderNavKey]?.pageKey || navKey;
}

export function pageKeyToNavKey(pageKey: string): string {
  if (pageKey === "journal" || pageKey === "journalPost") return "blog";
  return pageKey;
}

export function defaultSlugForNavKey(navKey: string): string {
  const pageKey = navKeyToPageKey(navKey);
  const rest = publicPathRestForTemplatePage(pageKey);
  return rest ? `/${rest}` : "/";
}

/** Canvas `<EL path="nav.shop">` stores the label at `nav.shop.text`. */
export function navLabelEditKey(navKey: string): string {
  return `nav.${navKey}.text`;
}

/** Canvas `<EL>` stores the href at `nav.shop.href`. */
export function navHrefEditKey(navKey: string): string {
  return `nav.${navKey}.href`;
}

type EditGet = (path: string, def: string) => string;

function contentEditsGet(
  contentEdits: Record<string, string | null | undefined> | null | undefined,
): EditGet {
  const edits = contentEdits || {};
  return (path, def) => {
    if (!Object.prototype.hasOwnProperty.call(edits, path)) return def;
    const value = edits[path];
    return value == null ? def : String(value);
  };
}

/** Pages rail / mobile sheet label — same string as the live nav link. */
export function websitePageLabelFromEdits(
  pageKey: string,
  contentEdits: Record<string, string | null | undefined> | null | undefined,
  customLabel?: string,
): string {
  const fallback = String(customLabel || websiteEditorPageLabel(pageKey) || pageKey).trim();
  const resolved = resolveNavLabel(
    contentEditsGet(contentEdits),
    pageKeyToNavKey(pageKey),
    fallback,
  ).trim();
  return resolved || fallback;
}

/** Pages rail / mobile sheet URL path — same slug the nav inspector saved locally. */
export function websitePageSlugFromEdits(
  pageKey: string,
  contentEdits: Record<string, string | null | undefined> | null | undefined,
  customSlug?: string,
): string {
  const navKey = pageKeyToNavKey(pageKey);
  const fallback = customSlug
    ? `/${String(customSlug).replace(/^\//, "")}`
    : defaultSlugForNavKey(navKey);
  const href = String(
    resolveNavHref(contentEditsGet(contentEdits), navKey, fallback) || "",
  ).trim();
  if (!href || href === "/") return fallback;
  return href.startsWith("/") ? href : `/${href}`;
}

/** Copy / open URL for a page, including a locally edited nav slug. */
export function websitePageShareUrl(
  username: string | undefined,
  pageKey: string,
  contentEdits: Record<string, string | null | undefined> | null | undefined,
  customSlug?: string,
  opts?: WebsitePageUrlOpts,
): string {
  return websitePageTabUrl(
    username,
    websitePageSlugFromEdits(pageKey, contentEdits, customSlug),
    opts,
  );
}

/** Prefer the live canvas key, then the older inspector-only key, then the default. */
export function resolveNavLabel(
  get: EditGet,
  navKey: string,
  fallback: string,
): string {
  const fromText = get(navLabelEditKey(navKey), "");
  const fromBare = get(`nav.${navKey}`, "");
  const textTrim = String(fromText).trim();
  const bareTrim = String(fromBare).trim();
  if (textTrim && bareTrim && textTrim !== bareTrim) {
    // Inline canvas edits write `nav.*.text`; prefer that over legacy bare keys.
    return fromText;
  }
  if (textTrim) return fromText;
  if (bareTrim) return fromBare;
  return fallback;
}

export function resolveNavHref(
  get: EditGet,
  navKey: string,
  fallback: string,
): string {
  const fromHref = get(navHrefEditKey(navKey), "");
  if (String(fromHref).trim()) return fromHref;
  const fromSlug = get(`nav.slug.${navKey}`, "");
  if (String(fromSlug).trim()) return fromSlug;
  return fallback;
}

export function parseNavOrder(raw: string | undefined | null): string[] {
  const fallback = [...SITE_HEADER_NAV_KEYS];
  if (!raw?.trim()) return pinSiteHeaderNavOrder(fallback);
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return pinSiteHeaderNavOrder(fallback);
    const allowed = new Set<string>(SITE_HEADER_NAV_KEYS);
    const next = parsed.filter(
      (k): k is string =>
        typeof k === "string" &&
        (allowed.has(k) || k.startsWith("c_")),
    );
    for (const k of SITE_HEADER_NAV_KEYS) {
      if (!next.includes(k)) next.push(k);
    }
    return pinSiteHeaderNavOrder(next.length ? next : fallback);
  } catch {
    return pinSiteHeaderNavOrder(fallback);
  }
}

export function serializeNavOrder(keys: string[]): string {
  return JSON.stringify(pinSiteHeaderNavOrder(keys));
}

export function parseNavOmit(raw: string | undefined | null): string[] {
  if (raw == null || !String(raw).trim()) return [...DEFAULT_NAV_OMIT];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...DEFAULT_NAV_OMIT];
    return parsed.filter((k): k is string => typeof k === "string" && k.length > 0);
  } catch {
    return [...DEFAULT_NAV_OMIT];
  }
}

export function serializeNavOmit(keys: string[]): string {
  return JSON.stringify(keys);
}

export type InspectorView = "root" | "navigation" | "categories" | "components" | "configure";

export type InspectorState =
  | { section: "Site Header"; view: "root" | "navigation"; editNavKey?: string }
  | { section: "Footer"; view: "root" }
  | { section: "Hero"; view: "root" }
  | { section: "addPage"; view: "root" }

  | {
      section: "add";
      view: "categories" | "components" | "configure";
      insertAt: number;
      category?: string;
      itemType?: string;
    }
  | {
      section: "component";
      view: "root";
      pageKey: string;
      sectionId: string;
      sectionType: string;
      label: string;
      idx: number;
      /** Set when this editor was opened by inserting from the add drawer. */
      fromAdd?: { insertAt: number; category?: string };
    };

export type DraftPreview = {
  type: string;
  title: string;
  desc?: string;
  shape?: string;
  platform?: string;
  /** Instagram show mode for content-feed (Posts & Reels / Stories / …). */
  show?: string;
  /**
   * Podcast-only: Carousel (multi-episode strip) vs Banner (single latest
   * episode) — same as main Stay Up To Date “Display as”.
   */
  podcastDisplay?: "Carousel" | "Banner";
  order?: string;
  /** Product feed timeframe (Latest / Yesterday / …) or trending date label. */
  timeframe?: string;
  /** Trending Content/Products `details` string for ghost RPC. */
  trendingDetails?: string;
  /** Shoppable Posts filter for feed ghosts. */
  shoppable?: string;
  /** Media Sound On/Off for hover-play in the add ghost. */
  sound?: string;
  /** Word/phrase for content-keyword / product-keyword ghosts. */
  phrase?: string;
  imageUrl?: string;
  cta?: string;
  link?: string;
  /** Banner / section layout cfg mirrored from the inspector. */
  cfg?: SectionCfg;
  /** Hand-picked posts/products for custom content/products ghost. */
  selectedMedia?: {
    id: string;
    image: string;
    title: string;
    shoppable?: boolean;
    platform?: string;
    isText?: boolean;
    postDate?: string;
  }[];
  /**
   * Link Section add-flow: `user_links.collection_name` (usually the heading)
   * so the ghost can live-preview real rows.
   */
  linksCollection?: string;
  /** `user_links.type` surface for the ghost (`website-template` / `landing-page`). */
  linksFrom?: string;
  /** Creator id for Link Section ghost queries (CreatorData can lag on LiB). */
  linksUserId?: string;
  /** Selected campaign button label for the Link in Bio add ghost. */
  campaignButtonText?: string;
  /** Selected campaign form fields for the Link in Bio add ghost. */
  campaignFields?: Array<{ label?: string; required?: boolean }>;
};

export type CatalogItem = {
  type: string;
  name: string;
  desc: string;
  category: string;
  platformToggle?: boolean;
  phrase?: boolean;
  /** Show image upload in the add-configure inspector. */
  needsImage?: boolean;
  /** Image is required before Add. */
  imageRequired?: boolean;
  /** Show CTA link field. */
  needsLink?: boolean;
  /** Show CTA / button label field. */
  needsCta?: boolean;
  /** Show embed HTML field. */
  needsEmbed?: boolean;
  /** Hide from Add Component picker (type still works for edit/chrome). */
  hiddenFromAdd?: boolean;
  settings?: { label: string; options: string[] }[];
};

const ORDER = {
  label: "Order",
  options: ["Newest to Oldest", "Oldest to Newest"],
};
/** Product / Recent Products timeframe — matches LiB (date windows only, no open-ended Latest). */
const TIMEFRAME = {
  label: "Timeframe",
  options: ["Yesterday", "This Week", "This Month", "By the Day"],
};
/** Recent Content timeframe — matches production Add Component (All = unlimited). */
export const CONTENT_FEED_TIMEFRAME = {
  label: "Timeframe",
  options: ["All", "Yesterday", "This Week", "This Month", "By the Day"],
};
export const CONTENT_FEED_DAY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "This Weekend",
] as const;
const TRENDING_DATE_RANGE = {
  label: "Date Range",
  options: [
    "Last 7 days",
    "Last 30 days",
    "Last 90 days",
    "All time",
    "Custom",
  ],
};
const BUILDER_SETTINGS = [
  ORDER,
  { label: "Shoppable Posts", options: ["All", "Shoppable", "Non-Shoppable"] },
  { label: "Media Sound", options: ["On", "Off"] },
];
/** Product feeds are shoppable-only (LiB) — no Shoppable Posts control. */
const PRODUCT_BUILDER_SETTINGS = [
  ORDER,
  { label: "Media Sound", options: ["On", "Off"] },
];
/** Trending content — date window + order/sound. */
const TRENDING_SETTINGS = [
  TRENDING_DATE_RANGE,
  ORDER,
  { label: "Media Sound", options: ["On", "Off"] },
];
/** Trending products — date window + order/sound (match legacy LiB). */
const PRODUCT_TRENDING_SETTINGS = [
  TRENDING_DATE_RANGE,
  ORDER,
  { label: "Media Sound", options: ["On", "Off"] },
];
/** Hand-picks: newest/oldest plus the same feed filters as edit. */
const CUSTOM_PICK_SETTINGS = [
  ORDER,
  { label: "Shoppable Posts", options: ["All", "Shoppable", "Non-Shoppable"] },
  { label: "Media Sound", options: ["On", "Off"] },
];
/** Custom Products — already shoppable picks; no Shoppable Posts filter. */
const CUSTOM_PRODUCTS_SETTINGS = [ORDER];

/** Banner layout / chrome — mirrors BannerSection.options. */
export const BANNER_ADD_SETTINGS = [
  {
    label: "Style",
    options: ["Overlay left", "Contained", "Ribbon", "Overlay center"],
  },
  { label: "Show eyebrow", options: ["Off", "On"] },
  { label: "Show subtitle", options: ["On", "Off"] },
  { label: "Show button", options: ["On", "Off"] },
  // Background is shown in Banner add/edit UI for Contained / Ribbon;
  // hidden for Overlay left and Overlay center.
];

export const HIGHLIGHT_ADD_SETTINGS = [
  { label: "Background", options: ["bg", "surface", "accent"] },
];

export const LINK_SECTION_ADD_SETTINGS = [
  { label: "Layout", options: ["list", "grid", "cards", "filled", "outline"] },
  { label: "Show thumbnail", options: ["On", "Off"] },
  { label: "Background", options: ["bg", "surface", "accent"] },
];

export const MEDIA_SETTINGS = [
  { label: "Shoppable Posts", options: ["All", "Shoppable", "Non-Shoppable"] },
  { label: "Media Sound", options: ["On", "Off"] },
];

export const ADD_CATEGORY_META: Record<string, { desc: string; icon: string }> = {
  Featured: { desc: "Highlight categories or collections", icon: "★" },
  Content: { desc: "Show your latest posts", icon: "▦" },
  Products: { desc: "Show your latest products", icon: "🏷️" },
  "Links & Features": { desc: "Buttons, banners, callouts", icon: "🔗" },
  Group: {
    desc: "Combine components into one section",
    icon: "▤",
  },
  "Text & Media": { desc: "Text, photo, and video blocks", icon: "✎" },
  Embed: { desc: "Code, newsletters & forms", icon: "</>" },
  Campaigns: { desc: "Email campaigns on your page", icon: "✉" },
  FAQ: { desc: "A list of questions and answers", icon: "?" },
  Testimonials: { desc: "Quotes and reviews from your audience", icon: "❝" },
  "Creator Tools": { desc: "Codes, featured in, and follower count", icon: "🎥" },
  "Layout & Structure": { desc: "Organize content on the page", icon: "▤" },
};

export const FEATURED_BAND_ADD_SETTINGS = [
  {
    label: "How many",
    options: ["2", "1", "3", "4"],
  },
  {
    label: "Images per tile",
    options: ["1 image", "Grid"],
  },
];

export const COMPONENT_CATALOG: Record<string, CatalogItem[]> = {
  Featured: [
    {
      type: "featured-categories",
      name: "Featured Categories",
      desc: "Hero, Split, Trio, or Quad band of shop categories",
      category: "Featured",
      settings: FEATURED_BAND_ADD_SETTINGS,
    },
    {
      type: "featured-collections",
      name: "Featured Collections",
      desc: "Hero → Trio band of collections you choose",
      category: "Featured",
      settings: FEATURED_BAND_ADD_SETTINGS,
    },
    {
      type: "featured-outfit",
      name: "Featured Outfit",
      desc: "Pin a post whose shop link is one of your collections",
      category: "Featured",
    },
    {
      type: "recent-looks",
      name: "Recent Looks",
      desc: "Posts that shop one of your collections — click to see the products",
      category: "Featured",
    },
  ],
  Content: [
    {
      type: "content-feed",
      name: "Recent Content",
      desc: "Auto-updates with your latest content from the platform you choose",
      category: "Content",
      platformToggle: true,
      settings: [CONTENT_FEED_TIMEFRAME, ...MEDIA_SETTINGS],
    },
    {
      type: "trending-content",
      name: "Trending Content",
      desc: "Auto-updates with your most-clicked content",
      category: "Content",
      settings: TRENDING_SETTINGS,
    },
    {
      type: "content-keyword",
      name: "Word or Phrase",
      desc: "Auto-updates as you publish new content matching your word or phrase",
      category: "Content",
      phrase: true,
      settings: BUILDER_SETTINGS,
    },
    {
      type: "custom-content",
      name: "Custom Content",
      desc: "Hand-pick the exact posts you want to show",
      category: "Content",
      settings: CUSTOM_PICK_SETTINGS,
    },
    // Hidden from Add picker (parity with production TEMPORARILY_HIDDEN) —
    // kept so edit/chrome helpers still resolve the type.
    {
      type: "this-week-content",
      name: "This Week's Content",
      desc: "Auto-updates with everything you posted in the last 7 days",
      category: "Content",
      settings: BUILDER_SETTINGS,
      hiddenFromAdd: true,
    },
    {
      type: "featured-content",
      name: "Featured",
      desc: "Shows all content you've marked as Featured",
      category: "Content",
      settings: BUILDER_SETTINGS,
    },
  ],
  Products: [
    {
      type: "product-feed",
      name: "Product Feed",
      desc: "Auto-updates with your latest products from a timeframe you choose",
      category: "Products",
      // Products-only — no Content/Both Show scope (production Recent Products).
      settings: [TIMEFRAME, ...PRODUCT_BUILDER_SETTINGS],
    },
    {
      type: "trending-products",
      name: "Trending Products",
      desc: "Auto-updates with your most-clicked products",
      category: "Products",
      settings: PRODUCT_TRENDING_SETTINGS,
    },
    {
      type: "product-keyword",
      name: "Word or Phrase",
      desc: "Auto-updates with products matching your word or phrase",
      category: "Products",
      phrase: true,
      settings: PRODUCT_BUILDER_SETTINGS,
    },
    {
      type: "custom-products",
      name: "Custom Products",
      desc: "Hand-pick from any of your shoppable products",
      category: "Products",
      settings: CUSTOM_PRODUCTS_SETTINGS,
    },
    {
      type: "store-products",
      name: "Digital Product",
      desc: "Add a published product from My Store. Checkout and the success page stay the same.",
      category: "Products",
    },
    {
      type: "featured-category",
      name: "Featured Category",
      desc: "Full-width banner of a primary category or subcategory and its children",
      category: "Products",
      hiddenFromAdd: true,
    },
    {
      type: "featured-brands",
      name: "Featured Brands",
      desc: "Showcase up to five processed brands",
      category: "Products",
      hiddenFromAdd: true,
    },
    {
      type: "shop-brand",
      name: "Shop a Brand",
      desc: "Feature one brand and its products",
      category: "Products",
      hiddenFromAdd: true,
    },
  ],
  /* Order matches Links & Features Drawers.html pick list. */
  "Links & Features": [
    {
      type: "banner",
      name: "Banner",
      desc: "A full-width image with a link",
      category: "Links & Features",
      needsImage: true,
      imageRequired: true,
      needsLink: true,
      needsCta: true,
      settings: BANNER_ADD_SETTINGS,
    },
    {
      type: "highlight",
      name: "Highlight",
      desc: "Call attention to one thing at the top of your page",
      category: "Links & Features",
      needsImage: true,
      imageRequired: true,
      needsLink: true,
      needsCta: true,
      settings: HIGHLIGHT_ADD_SETTINGS,
    },
    {
      type: "cta",
      name: "Button",
      desc: "A single call-to-action button",
      category: "Links & Features",
      needsLink: true,
      needsCta: true,
    },
    {
      type: "ribbon",
      name: "Ribbon / Alert",
      desc: "A thin announcement bar across the page",
      category: "Links & Features",
    },
    {
      type: "link-section",
      name: "Link Section",
      desc: "A group of link buttons on your page",
      category: "Links & Features",
      settings: LINK_SECTION_ADD_SETTINGS,
    },
  ],
  Group: [
    {
      type: "group",
      name: "Group Components",
      desc: "Combine components you've built into one section on your page",
      category: "Group",
    },
  ],
  /* Order matches Text & Media Drawers.html pick list. */
  "Text & Media": [
    {
      type: "page-header",
      name: "Header",
      desc: "Page title, subtext, and background",
      category: "Text & Media",
    },
    {
      type: "text",
      name: "Text",
      desc: "Rich text with an optional title",
      category: "Text & Media",
    },
    {
      type: "quote",
      name: "Quote",
      desc: "Styled quotation",
      category: "Text & Media",
    },
    {
      type: "spacer",
      name: "Spacer / Divider",
      desc: "Space or a line",
      category: "Text & Media",
    },
    {
      type: "image",
      name: "Image / Gallery",
      desc: "Single photo or gallery",
      category: "Text & Media",
      imageRequired: true,
    },
  ],
  Embed: [
    {
      type: "embed",
      name: "Embed Custom Code",
      desc: "Drop in your own HTML or iframe embed code",
      category: "Embed",
      needsEmbed: true,
    },
    {
      type: "embed-newsletter-kit",
      name: "Embed Newsletter Kit",
      desc: "Embed a newsletter signup kit on your page",
      category: "Embed",
      needsEmbed: true,
    },
    {
      type: "flodesk",
      name: "Flodesk",
      desc: "Add an inline or popup newsletter form",
      category: "Embed",
      settings: [
        {
          label: "Newsletter Type",
          options: ["Inline", "Popup", "Both"],
        },
      ],
    },
    {
      type: "fillout",
      name: "Fillout",
      desc: "Add your Fillout form",
      category: "Embed",
    },
  ],
  Campaigns: [
    {
      type: "campaign",
      name: "Campaign",
      desc: "Add an active campaign to your page",
      category: "Campaigns",
    },
  ],
  FAQ: [
    {
      type: "accordion",
      name: "FAQ",
      desc: "A list of questions and answers",
      category: "FAQ",
    },
  ],
  Testimonials: [
    {
      type: "testimonials",
      name: "Testimonials",
      desc: "Quotes and reviews from your audience",
      category: "Testimonials",
    },
  ],
  /* Order matches Creator Tools Drawers.html pick list first. */
  "Creator Tools": [
    {
      type: "discount",
      name: "Discount Codes",
      desc: "Show your active promo codes",
      category: "Creator Tools",
    },
    {
      type: "asseenon",
      name: "As Seen On",
      desc: "Logos and press you've been featured in",
      category: "Creator Tools",
    },
    {
      type: "followers",
      name: "Follower Counts",
      desc: "Your audience across platforms",
      category: "Creator Tools",
    },
    {
      type: "map",
      name: "Map",
      desc: "A map pinned to an address",
      category: "Creator Tools",
    },
    {
      type: "video",
      name: "Video Player",
      desc: "Embedded video, not a generic embed",
      category: "Creator Tools",
    },
  ],
  "Layout & Structure": [
    {
      type: "sectionheader",
      name: "Section Header",
      desc: "Eyebrow, headline & subtext",
      category: "Layout & Structure",
    },
    {
      type: "columns",
      name: "Content Section",
      desc: "1–3 columns of content",
      category: "Layout & Structure",
    },
    {
      type: "tabs",
      name: "Tabs",
      desc: "Switch between content panels",
      category: "Layout & Structure",
      // Edit modal UI needs cleanup — hide from Add for now; existing Tabs still edit.
      hiddenFromAdd: true,
    },
    // Intentionally hidden from the picker (not dead / unused code). Custom
    // Section (type "vibe") is still implemented and we will keep working on
    // it. Do not delete this entry, the vibe inspector, or generation code.
    // {
    //   type: "vibe",
    //   name: "Custom Section",
    //   desc: "Describe it and we build it for you",
    //   category: "Layout & Structure",
    // },
  ],
};

/** Shared Add-drawer placeholder when a type has no default title. */
export const ADD_COMPONENT_TITLE_PLACEHOLDER = "Enter a title";

/** Prefill Add drawer title + ghost preview with the component type name. */
export const DEFAULT_COMPONENT_TITLES: Record<string, string> = {
  "trending-content": "Trending Content",
  // Match ghost preview fallback when no custom title is set.
  "content-keyword": "New component",
  "custom-content": "My picks",
  "this-week-content": "This Week's Content",
  "featured-content": "Featured Content",
  "product-feed": "Recent Products",
  "content-feed": "Recent Content",
  "trending-products": "Trending Products",
  "featured-category": "Featured Category",
  "featured-categories": "Featured Categories",
  "featured-collections": "Featured Collections",
  "featured-brands": "Featured Brands",
  "shop-brand": "Shop Brand",
  "featured-outfit": "Featured Outfit",
  "recent-looks": "Recent Looks",
  "product-keyword": "New component",
  "custom-products": "My favorites",
  "store-products": "Digital product",
  "link-section": "My Links",
  highlight: "Don't miss this",
  banner: "Banner",
  group: "Collection of Carousel",
  embed: "Embed",
  "embed-newsletter-kit": "Newsletter Kit",
  flodesk: "Join my list",
  fillout: "Form",
  campaign: "Campaign",
  cta: "Button",
  ribbon: "Announcement",
  text: "Text",
  image: "Gallery",
  quote: "Quote",
  spacer: "Spacer",
  video: "Video",
  discount: "Codes",
  testimonials: "What people say",
  followers: "Followers",
  asseenon: "As Seen On",
  map: "Map",
  accordion: "FAQ",
  sectionheader: "Section Header",
  "page-header": "Header",
  columns: "Content Section",
  tabs: "Tabs",
  vibe: "Custom section",
  newsletter: "Get the weekly edit",
};

export const FEED_PLATFORMS = [
  "Instagram",
  "TikTok",
  "YouTube",
  "X",
  "LinkedIn",
  "Podcast",
] as const;

/** Product phrase carousel — platforms that carry shoppable product posts. */
export const PRODUCT_FEED_PLATFORM_LABELS = [
  "LTK",
  "ShopMy",
  "Amazon",
  "Added By Me",
] as const;

export function productFeedPlatformLabel(raw?: string): string {
  const s = String(raw || "All").trim();
  if (!s || /^all$/i.test(s)) return "All";
  if (/^misc(?:[-_](?:product|content|agg))?$/i.test(s)) return "Added By Me";
  const hit = StaticVariables().PRODUCT_PLATFORMS.find(
    (p) => p.toLowerCase() === s.toLowerCase(),
  );
  return hit || "All";
}

/** Keys stored in `details.platformFilter` for product phrase feeds. */
export function productFeedPlatformValue(label: string): string {
  const s = String(label || "All").trim() || "All";
  if (/^all$/i.test(s)) return "All";
  if (/^added by me$/i.test(s)) return "MISC";
  const hit = StaticVariables().PRODUCT_PLATFORMS.find(
    (p) => p.toLowerCase() === s.toLowerCase(),
  );
  return hit || "All";
}

export function feedPlatformAliases(platform: string): string[] {
  const map: Record<string, string[]> = {
    Instagram: ["instagram", "ig", "reels"],
    TikTok: ["tiktok"],
    YouTube: ["youtube", "yt"],
    X: ["x", "twitter"],
    LinkedIn: ["linkedin"],
    Podcast: ["podcast", "spotify", "apple podcasts", "applepodcast"],
  };
  return map[platform] || [platform.toLowerCase()];
}

/**
 * Whether a Content Feed platform is connected — mirrors Connected Platforms
 * (`IntegrationsBox` / `handleConnected`), not social link URLs or carousel
 * title substring matches (those falsely enabled X / LinkedIn).
 */
export function isWebsiteFeedPlatformConnected(
  social: { platform: string }[] | undefined,
  platform: string,
  _carousels?: unknown,
  connectedPlatforms?: Partial<Record<string, boolean>>,
): boolean {
  if (connectedPlatforms && Object.keys(connectedPlatforms).length > 0) {
    if (Object.prototype.hasOwnProperty.call(connectedPlatforms, platform)) {
      return Boolean(connectedPlatforms[platform]);
    }
  }
  // Fallback when bio.connectedPlatforms is missing (older creator snapshots).
  const aliases = feedPlatformAliases(platform);
  const aliasSet = new Set(aliases);
  return (social || []).some((s) =>
    aliasSet.has(String(s.platform || "").toLowerCase()),
  );
}

/** Content-page browse slug → Connected Platforms / FEED_PLATFORMS label. */
export const CONTENT_BROWSE_SLUG_TO_FEED_PLATFORM: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  twitter: "X",
  linkedin: "LinkedIn",
  podcast: "Podcast",
};

/** Fixed ribbon order — do not sort by live post counts (those shift as facets load). */
const CONTENT_PLATFORM_BUTTON_ORDER = [
  "instagram",
  "tiktok",
  "youtube",
  "twitter",
  "x",
  "linkedin",
  "podcast",
];

export function compareContentPlatformButtons(aSlug: string, bSlug: string): number {
  const rank = (slug: string) => {
    const s = String(slug || "").trim().toLowerCase();
    const i = CONTENT_PLATFORM_BUTTON_ORDER.indexOf(s);
    return i === -1 ? CONTENT_PLATFORM_BUTTON_ORDER.length : i;
  };
  const delta = rank(aSlug) - rank(bSlug);
  if (delta !== 0) return delta;
  return String(aSlug || "").localeCompare(String(bSlug || ""));
}

/** True for content platform / Other browse path segments (not product categories). */
export function isContentPlatformBrowseSlug(slug: string): boolean {
  const s = String(slug || "").trim().toLowerCase();
  if (!s) return false;
  if (s === "others" || s === "other" || s === "misc" || s === "misc-content") {
    return true;
  }
  return Boolean(CONTENT_BROWSE_SLUG_TO_FEED_PLATFORM[s]);
}

/**
 * Content page platform ribbon: only creator-connected platforms.
 * "Other" / MISC-content is excluded from the browse chrome (still in facets
 * for totals; deep-links to it clear back to all content).
 */
export function isContentBrowsePlatformVisible(
  slug: string,
  social: { platform: string }[] | undefined,
  connectedPlatforms?: Partial<Record<string, boolean>>,
): boolean {
  const s = String(slug || "").trim().toLowerCase();
  if (!s) return false;
  // Hide Other from the Content platform categories ribbon.
  if (s === "others" || s === "other" || s === "misc" || s === "misc-content") {
    return false;
  }
  const feedName = CONTENT_BROWSE_SLUG_TO_FEED_PLATFORM[s];
  if (!feedName) return false;
  return isWebsiteFeedPlatformConnected(
    social,
    feedName,
    undefined,
    connectedPlatforms,
  );
}

export function feedDefaultTitle(
  platform: string,
  show: string,
  podcastDisplay?: string,
): string {
  if (platform === "All") {
    return "Latest Content";
  }
  if (platform === "Instagram") {
    if (show === "All") return "Latest Instagram";
    if (show === "Reels") return "Latest Instagram Reels";
    if (show === "Posts") return "Latest Instagram Posts";
    if (show === "Stories") return "Latest Instagram Stories";
    return "Latest Instagram Posts & Reels";
  }
  if (platform === "Podcast") {
    // Match main Stay Up To Date: Banner = single episode, Carousel = strip.
    return podcastDisplay === "Banner"
      ? "Latest Podcast Episode"
      : "Latest Podcast Episodes";
  }
  const map: Record<string, string> = {
    TikTok: "Latest TikToks",
    YouTube: "Latest YouTube Videos",
    X: "Latest X Posts",
    LinkedIn: "Latest LinkedIn Posts",
  };
  return map[platform] || `Latest ${platform}`;
}

export function findCatalogItem(type?: string): CatalogItem | null {
  if (!type) return null;
  for (const items of Object.values(COMPONENT_CATALOG)) {
    const found = items.find((i) => i.type === type);
    if (found) return found;
  }
  return null;
}

/**
 * Default title for an Add catalog type (drawer + preview).
 * Prefers the type map, then the catalog display name, so the field is never blank.
 */
export function defaultComponentTitle(
  type: string,
  fallbackName?: string,
): string {
  const mapped = DEFAULT_COMPONENT_TITLES[type];
  if (mapped) return mapped;
  const fromCatalog = findCatalogItem(type)?.name;
  return String(fallbackName || fromCatalog || "").trim();
}

export function websiteNavPageKeysForRail(): readonly string[] {
  return WEBSITE_NAV_PAGE_KEYS;
}

export function inspectorPageLabel(pageKey: string): string {
  return websiteEditorPageLabel(pageKey);
}
